import type { UrlTargeting } from '@openx/types/targeting/domains';
import { type ChangeEvent, useCallback, useEffect, useReducer, useRef } from 'react';
import { DOMAINS_LIMIT } from '../../../Domains/constants';
import type { ValidationResult } from '../../BulkAdd/types';
import { BULK_SPLITER } from '../../BulkAdd/utils';
import { dispatchChangeHandler, dispatchReset, dispatchSetValidating, dispatchValidate } from './actions';
import { reducer } from './reducer';
import { initialReducerState } from './types';

type UseBulkReducerProps = {
  validate: (value: string[], inventoryListItems?: string[]) => ValidationResult;
  inventoryListItems?: string[];
  handleReset: () => UrlTargeting;
  onApply: (items: string[]) => void;
  validatedTextInput?: string;
  setApplyEnabled: (value: boolean) => void;
  isBlacklist?: boolean;
};

const getInitialState = (initItems?: string, isBlacklist?: boolean) => {
  return {
    ...initialReducerState,
    isBlacklist,
    value: initItems || '',
    validItems: initItems
      ? initItems
          .split(BULK_SPLITER)
          .map(value => value.trim())
          .filter(value => Boolean(value))
      : [],
  };
};

export const useBulkReducer = ({
  validate,
  inventoryListItems,
  handleReset,
  onApply,
  validatedTextInput,
  setApplyEnabled,
  isBlacklist,
}: UseBulkReducerProps) => {
  const initialState = useRef(getInitialState(validatedTextInput, isBlacklist));
  const [bulk, dispatch] = useReducer(reducer, initialState.current);

  useEffect(() => {
    if (!validatedTextInput) {
      dispatchChangeHandler(dispatch, '');
    }
  }, [validatedTextInput]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      dispatchChangeHandler(dispatch, event.target.value);

      const textInputValue = event.target.value;
      const shouldSetApplyEnabled = textInputValue !== validatedTextInput && textInputValue !== '';

      if (shouldSetApplyEnabled) {
        setApplyEnabled(false);
      }
    },
    [validatedTextInput, setApplyEnabled]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  const onValidate = useCallback(
    async (hasInventoryListChanged: boolean) => {
      dispatchSetValidating(dispatch, true);

      const items = bulk.value
        .split(BULK_SPLITER)
        .map(value => value.trim())
        .filter(value => Boolean(value));

      const { errors, validItems } = await validate(items, inventoryListItems);

      if (hasInventoryListChanged) {
        setApplyEnabled(true);
        onApply(validItems);
        dispatchValidate(dispatch, errors, validItems);

        return;
      }

      const shouldBlockApply = (validItems.length === 0 && bulk.value) || validItems.length > DOMAINS_LIMIT;

      if (shouldBlockApply) {
        setApplyEnabled(false);
      } else {
        setApplyEnabled(true);
      }

      onApply(validItems);
      dispatchValidate(dispatch, errors, validItems);
    },
    [bulk.value, validate, onApply, inventoryListItems, setApplyEnabled, bulk.validItems, isBlacklist]
  );

  const onReset = useCallback(() => {
    dispatchSetValidating(dispatch, true);

    const newInitValue = handleReset();

    dispatchReset(dispatch, newInitValue?.urls?.join('\n') ?? '');
  }, [handleReset]);

  return {
    bulk,
    onValidate,
    onReset,
    onChange,
    dispatch,
  };
};
